export type TGridSelModelIdentifier = string | number;

export interface ISelectionModel {
  selectedIds: TGridSelModelIdentifier[];
  lastIndexOperation: number;
  lastOperationIsSelection: boolean;
  lastSingleSelectedId: TGridSelModelIdentifier;
}

export const emptySelectionModel: ISelectionModel = {
  selectedIds: [],
  lastIndexOperation: 0,
  lastOperationIsSelection: true,
  lastSingleSelectedId: null,
};

export function createSelectionModel(selectedIds: TGridSelModelIdentifier[]): ISelectionModel {
  return {
    ...emptySelectionModel,
    selectedIds: selectedIds.slice(0),
  };
}

export function cloneSelectionModel(selectionModel: ISelectionModel): ISelectionModel {
  return {
    selectedIds: selectionModel.selectedIds.slice(0),
    lastIndexOperation: selectionModel.lastIndexOperation,
    lastOperationIsSelection: selectionModel.lastOperationIsSelection,
    lastSingleSelectedId: selectionModel.lastSingleSelectedId,
  };
}

export function setSelected(
  selectionModel: ISelectionModel,
  id: TGridSelModelIdentifier,
  index: number = null,
  selected: boolean,
  exclusiveSelection?: boolean,
): ISelectionModel {
  selectionModel.lastIndexOperation = index;
  selectionModel.lastOperationIsSelection = selected;

  // if we do allow just one single selection, clear the selectedIds before proceeding
  if (exclusiveSelection) {
    selectionModel.selectedIds = [];
  }

  if (selectionModel.selectedIds.length === 0 && !!selected) {
    selectionModel.lastSingleSelectedId = id;
  }

  const idIndex: number = selectionModel.selectedIds.indexOf(id);
  if (selected) {
    // prevent to add the object twice to the selection e.g. with range selection,
    // can lead to  issues in case of range unselection
    // as a performance boost we could use objects instead of arrays to make the id anyway unique
    if (idIndex === -1) {
      selectionModel.selectedIds.push(id);
    }
  } else {
    if (idIndex > -1) {
      selectionModel.selectedIds.splice(idIndex, 1);
    }
  }
  return selectionModel;
}

export function setMultipleSelected(
  selectionModel: ISelectionModel,
  ids: TGridSelModelIdentifier[],
  selected: boolean,
): void {
  selectionModel.lastIndexOperation = -1;
  selectionModel.lastOperationIsSelection = null;
  ids.forEach((id: number) => {
    const idIndex: number = selectionModel.selectedIds.indexOf(id);
    if (selected) {
      // prevent to add the object twice to the selection e.g. with range selection,
      // can lead to  issues in case of range unselection
      // as a performance boost we could use objects instead of arrays to make the id anyway unique
      if (idIndex === -1) {
        selectionModel.selectedIds.push(id);
      }
    } else {
      if (idIndex > -1) {
        selectionModel.selectedIds.splice(idIndex, 1);
      }
    }
  });
}

// rangeIndex: shift clicked row index
export function setRangeSelected(
  selectionModel: ISelectionModel,
  index: number,
  allIds: TGridSelModelIdentifier[],
  selected?: boolean,
): ISelectionModel {
  if (selected === undefined) {
    selected = selectionModel.lastOperationIsSelection;
  }

  const { rangeStart, rangeStop } = computeSelectionRange(selectionModel, index);

  for (let pos = rangeStart; pos <= rangeStop; pos++) {
    setSelected(selectionModel, allIds[pos], pos, selected);
  }

  return selectionModel;
}

export function computeSelectionRange(
  selectionModel: ISelectionModel,
  index: number,
): { rangeStart: number; rangeStop: number } {
  let rangeStart: number;
  let rangeStop: number;

  if (selectionModel.lastIndexOperation < index) {
    rangeStart = selectionModel.lastIndexOperation;
    rangeStop = index;
  } else {
    rangeStart = index;
    rangeStop = selectionModel.lastIndexOperation;
  }

  return { rangeStart, rangeStop };
}

export function setAllSelected(
  selectionModel: ISelectionModel,
  ids: TGridSelModelIdentifier[] = [],
  selected = false,
): ISelectionModel {
  if (selected) {
    selectionModel.selectedIds = ids.slice(0);
  } else {
    selectionModel.selectedIds = [];
  }

  return selectionModel;
}

export function resetSelection(selectionModel: ISelectionModel) {
  selectionModel.selectedIds = [];
  return selectionModel;
}

export function checkAvailabilityOfIdentifiers(
  allIds: TGridSelModelIdentifier[],
  rangeStart: number,
  rangeStop: number,
) {
  // we start the check from the top as it is more likely to return false immediately
  for (let index: number = rangeStop; index >= rangeStart; index--) {
    if (!allIds[index]) {
      return false;
    }
  }

  return true;
}

export function isSelected(selectionModel: ISelectionModel, id: TGridSelModelIdentifier): boolean {
  return selectionModel.selectedIds.includes(id);
}
